import React, { useState, useEffect, useRef } from 'react';
import { MessageCircle, X, Send } from 'lucide-react';
import axios from 'axios';

const ChatbotWidget = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [inputMessage, setInputMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [sessionId] = useState(() => `session-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`);
  const [services, setServices] = useState([]);
  const [showServiceButtons, setShowServiceButtons] = useState(false);
  const [selectedService, setSelectedService] = useState(null);
  const [showLeadForm, setShowLeadForm] = useState(false);
  const [leadData, setLeadData] = useState({ name: '', email: '', phone: '', company: '', destination_country: '', schengen_country: '' });
  const [countries, setCountries] = useState([]);
  const [schengenCountries, setSchengenCountries] = useState([]);
  const [showSchengenDropdown, setShowSchengenDropdown] = useState(false);
  const [leadCreated, setLeadCreated] = useState(false);
  const [agentConnected, setAgentConnected] = useState(false);
  const [agentName, setAgentName] = useState('');
  const [waitingForAgent, setWaitingForAgent] = useState(false);
  const [showAgentOption, setShowAgentOption] = useState(false);
  const messagesEndRef = useRef(null);
  const pollingInterval = useRef(null);
  const backendURL = process.env.REACT_APP_BACKEND_URL || '';

  useEffect(() => {
    fetchServices();
    // Don't fetch countries on mount - fetch when service is selected
    return () => {
      if (pollingInterval.current) {
        clearInterval(pollingInterval.current);
      }
    };
  }, []);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const fetchServices = async () => {
    try {
      const response = await axios.get(`${backendURL}/api/services`);
      setServices(response.data.slice(0, 5)); // Top 5 services
    } catch (error) {
      console.error('Error fetching services:', error);
    }
  };

  const fetchCountriesForService = async (service) => {
    try {
      // Use countries from service object (comma-separated list)
      const serviceCountries = service.countries || [];
      
      // Convert to format expected by dropdown
      const countryList = serviceCountries.filter(Boolean).map(name => ({ name }));
      
      setCountries(countryList);
      
      if (countryList.length === 0) {
        // If no countries available for this service, show a message
        setMessages(prev => [...prev, {
          role: 'bot',
          content: `This service is currently not available in any countries. Please contact us for more information.`,
          timestamp: new Date()
        }]);
      }
    } catch (error) {
      console.error('Error fetching countries for service:', error);
      setCountries([]);
    }
  };

  const fetchSchengenCountries = async () => {
    try {
      const response = await axios.get(`${backendURL}/api/schengen-countries`);
      setSchengenCountries(response.data || []);
    } catch (error) {
      console.error('Error fetching Schengen countries:', error);
      setSchengenCountries([]);
    }
  };

  const checkAvailableAgents = async () => {
    try {
      const response = await axios.get(`${backendURL}/api/chatbot/agent/available`);
      // Return true only if manual chat is enabled AND agents are available
      return response.data.manual_chat_enabled && response.data.agents_available;
    } catch (error) {
      console.error('Error checking agents:', error);
      return false;
    }
  };

  const startPolling = () => {
    // Only poll if we're actually waiting for or connected to an agent
    if (!waitingForAgent && !agentConnected) return;
    if (pollingInterval.current) return;
    
    pollingInterval.current = setInterval(async () => {
      try {
        // Only poll if session exists and we're in agent mode
        if (!sessionId) return;
        
        const response = await axios.get(`${backendURL}/api/chatbot/admin/chat/${sessionId}/messages`);
        const serverMessages = response.data || [];
        
        // Check for agent messages
        const agentMessages = serverMessages.filter(m => m.role === 'agent');
        if (agentMessages.length > 0 && !agentConnected) {
          const lastAgentMsg = agentMessages[agentMessages.length - 1];
          setAgentConnected(true);
          setAgentName(lastAgentMsg.agent_name || 'Agent');
          setWaitingForAgent(false);
        }
        
        // Update messages if different
        if (serverMessages.length > messages.length) {
          setMessages(serverMessages.map(m => ({
            role: m.role,
            content: m.content,
            timestamp: new Date(m.timestamp),
            agentName: m.agent_name
          })));
        }
      } catch (error) {
        // Silently fail - don't spam console with 404s
        if (error.response?.status !== 404) {
          console.error('Polling error:', error);
        }
        // Stop polling on persistent errors
        if (error.response?.status === 404 || error.response?.status === 401) {
          stopPolling();
        }
      }
    }, 3000);
  };

  const stopPolling = () => {
    if (pollingInterval.current) {
      clearInterval(pollingInterval.current);
      pollingInterval.current = null;
    }
  };

  const handleServiceSelect = async (service) => {
    setSelectedService(service);
    setShowServiceButtons(false);
    
    // Reset destination country when service changes
    setLeadData(prev => ({ ...prev, destination_country: '' }));
    
    const userMsg = {
      role: 'user',
      content: service.title,
      timestamp: new Date()
    };
    
    const botMsg = {
      role: 'assistant',
      content: 'Great! To help you better, please provide your contact details:',
      timestamp: new Date()
    };
    
    setMessages(prev => [...prev, userMsg, botMsg]);
    
    // Load countries from service object (no API call needed)
    await fetchCountriesForService(service);
    
    setShowLeadForm(true);
  };

  const handleLeadSubmit = async (e) => {
    e.preventDefault();
    
    if (!leadData.name || !leadData.email || !leadData.phone || !leadData.destination_country) {
      alert('Please fill in all required fields');
      return;
    }
    
    // If Schengen is selected, ensure a Schengen country is chosen
    if (showSchengenDropdown && !leadData.schengen_country) {
      alert('Please select a Schengen country');
      return;
    }

    setIsLoading(true);
    
    try {
      // Determine final destination country (use Schengen country if selected, otherwise primary)
      const finalDestinationCountry = showSchengenDropdown && leadData.schengen_country 
        ? leadData.schengen_country 
        : leadData.destination_country;
      
      // Create lead in CRM
      const leadResponse = await axios.post(`${backendURL}/api/leads`, {
        name: leadData.name,
        email: leadData.email,
        phone: leadData.phone,
        destination_country: finalDestinationCountry,
        service_type: selectedService?.title,
        source: 'chatbot',
        tags: ['chatbot_lead'],
        description: `Interested in: ${selectedService?.title}. ${leadData.company ? `Company: ${leadData.company}` : ''}`
      });

      setLeadCreated(true);
      setShowLeadForm(false);
      
      // Check if agents are available
      const hasAgents = await checkAvailableAgents();
      
      if (hasAgents) {
        // Show option to connect with agent
        setShowAgentOption(true);
        const optionMsg = {
          role: 'assistant',
          content: `Thank you ${leadData.name}! Your information has been received. Would you like to connect with one of our consultants now?`,
          timestamp: new Date()
        };
        setMessages(prev => [...prev, optionMsg]);
      } else {
        // No agents available
        const thankYouMsg = {
          role: 'assistant',
          content: `Thank you ${leadData.name}! Your information has been received. One of our consultants will reach out to you shortly.`,
          timestamp: new Date()
        };
        setMessages(prev => [...prev, thankYouMsg]);
      }
      
    } catch (error) {
      console.error('Error creating lead:', error);
      alert('Failed to submit. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleConnectToAgent = async () => {
    setShowAgentOption(false);
    setWaitingForAgent(true);
    
    const waitMsg = {
      role: 'assistant',
      content: 'Connecting you to a consultant...',
      timestamp: new Date()
    };
    setMessages(prev => [...prev, waitMsg]);
    
    try {
      // Create conversation and request agent assignment
      const conversationData = {
        session_id: sessionId,
        lead_name: leadData.name,
        lead_email: leadData.email,
        lead_phone: leadData.phone,
        service_interest: selectedService?.title,
        messages: messages.map(m => ({
          role: m.role,
          content: m.content,
          timestamp: m.timestamp.toISOString()
        })),
        status: 'waiting_for_agent'
      };
      
      const response = await axios.post(`${backendURL}/api/chatbot/conversation/request-agent`, conversationData);
      
      if (response.data.agent_name) {
        // Agent assigned immediately
        setAgentConnected(true);
        setAgentName(response.data.agent_name);
        setWaitingForAgent(false);
        
        const connectedMsg = {
          role: 'assistant',
          content: `You're now connected with ${response.data.agent_name}. They will respond shortly.`,
          timestamp: new Date()
        };
        setMessages(prev => [...prev, connectedMsg]);
      }
      
      // Start polling for agent messages
      // TODO: Implement live agent polling with proper endpoint
      // startPolling();
      
    } catch (error) {
      console.error('Error requesting agent:', error);
      const errorMsg = {
        role: 'assistant',
        content: 'Sorry, we encountered an issue connecting you. Please try again or contact us directly.',
        timestamp: new Date()
      };
      setMessages(prev => [...prev, errorMsg]);
      setWaitingForAgent(false);
    }
  };

  const handleDeclineAgent = () => {
    setShowAgentOption(false);
    const thankYouMsg = {
      role: 'assistant',
      content: 'No problem! One of our consultants will reach out to you shortly via email or phone.',
      timestamp: new Date()
    };
    setMessages(prev => [...prev, thankYouMsg]);
  };

  const sendMessage = async (e) => {
    e.preventDefault();
    if (!inputMessage.trim() || isLoading) return;

    const userMessage = {
      role: 'user',
      content: inputMessage,
      timestamp: new Date()
    };

    setMessages(prev => [...prev, userMessage]);
    const currentInput = inputMessage;
    setInputMessage('');

    // First message - show services
    if (messages.length === 0) {
      setTimeout(() => {
        const botMsg = {
          role: 'assistant',
          content: 'Hello! Welcome to Visaasure. How can we help you today? Please select a service:',
          timestamp: new Date()
        };
        setMessages(prev => [...prev, botMsg]);
        setShowServiceButtons(true);
      }, 500);
      return;
    }

    // If agent connected, send to backend
    if (agentConnected) {
      try {
        await axios.post(`${backendURL}/api/chatbot/conversation/${sessionId}/message`, {
          message: currentInput
        });
      } catch (error) {
        console.error('Error sending message:', error);
      }
    }
  };

  return (
    <>
      {/* Chat Button */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        style={{
          position: 'fixed',
          bottom: '20px',
          right: '20px',
          width: '60px',
          height: '60px',
          borderRadius: '50%',
          background: 'linear-gradient(135deg, #A08EA8 0%, #7D6882 100%)',
          border: 'none',
          boxShadow: '0 4px 12px rgba(160, 142, 168, 0.4)',
          cursor: 'pointer',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontSize: '28px',
          zIndex: 9999,
          transition: 'transform 0.2s, box-shadow 0.2s',
        }}
        onMouseEnter={(e) => {
          e.target.style.transform = 'scale(1.1)';
          e.target.style.boxShadow = '0 6px 16px rgba(160, 142, 168, 0.5)';
        }}
        onMouseLeave={(e) => {
          e.target.style.transform = 'scale(1)';
          e.target.style.boxShadow = '0 4px 12px rgba(160, 142, 168, 0.4)';
        }}
      >
        {isOpen ? <X size={24} color="white" /> : <MessageCircle size={24} color="white" />}
      </button>

      {/* Chat Window */}
      {isOpen && (
        <div
          style={{
            position: 'fixed',
            bottom: '90px',
            right: '20px',
            width: '380px',
            height: '550px',
            backgroundColor: 'white',
            borderRadius: '12px',
            boxShadow: '0 8px 24px rgba(0, 0, 0, 0.15)',
            display: 'flex',
            flexDirection: 'column',
            zIndex: 9998,
            overflow: 'hidden'
          }}
        >
          {/* Header */}
          <div
            style={{
              background: 'linear-gradient(135deg, #A08EA8 0%, #7D6882 100%)',
              color: 'white',
              padding: '16px',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              borderTopLeftRadius: '12px',
              borderTopRightRadius: '12px'
            }}
          >
            <div>
              <h3 style={{ margin: 0, fontSize: '16px', fontWeight: '600' }}>
                {agentConnected ? `Chat with ${agentName}` : 'Visaasure Support'}
              </h3>
              {agentConnected && (
                <p style={{ margin: 0, fontSize: '12px', opacity: 0.9 }}>Agent is online</p>
              )}
              {waitingForAgent && (
                <p style={{ margin: 0, fontSize: '12px', opacity: 0.9 }}>Waiting for agent...</p>
              )}
            </div>
            <button
              onClick={() => setIsOpen(false)}
              style={{
                background: 'none',
                border: 'none',
                color: 'white',
                cursor: 'pointer',
                padding: '4px'
              }}
            >
              <X size={20} />
            </button>
          </div>

          {/* Messages */}
          <div style={{ flex: 1, overflowY: 'auto', padding: '16px', background: '#f9fafb' }}>
            {messages.map((msg, index) => (
              <div
                key={index}
                style={{
                  marginBottom: '12px',
                  display: 'flex',
                  justifyContent: msg.role === 'user' ? 'flex-end' : 'flex-start'
                }}
              >
                <div
                  style={{
                    maxWidth: '70%',
                    padding: '10px 14px',
                    borderRadius: '12px',
                    backgroundColor: msg.role === 'user' ? '#A08EA8' : 'white',
                    color: msg.role === 'user' ? 'white' : '#333',
                    boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                    fontSize: '14px',
                    lineHeight: '1.4'
                  }}
                >
                  {msg.role === 'agent' && (
                    <div style={{ fontSize: '11px', color: '#A08EA8', marginBottom: '4px', fontWeight: '600' }}>
                      {msg.agentName}
                    </div>
                  )}
                  {msg.content}
                </div>
              </div>
            ))}

            {isLoading && (
              <div style={{ padding: '8px', display: 'flex', gap: '4px' }}>
                <span style={{ width: '8px', height: '8px', background: '#A08EA8', borderRadius: '50%' }}></span>
                <span style={{ width: '8px', height: '8px', background: '#A08EA8', borderRadius: '50%' }}></span>
                <span style={{ width: '8px', height: '8px', background: '#A08EA8', borderRadius: '50%' }}></span>
              </div>
            )}

            {/* Service Buttons */}
            {showServiceButtons && services.length > 0 && (
              <div style={{ marginTop: '12px', display: 'flex', flexDirection: 'column', gap: '8px' }}>
                {services.map((service) => (
                  <button
                    key={service.id}
                    onClick={() => handleServiceSelect(service)}
                    style={{
                      background: 'linear-gradient(135deg, #A08EA8 0%, #7D6882 100%)',
                      color: 'white',
                      border: 'none',
                      padding: '10px 14px',
                      borderRadius: '8px',
                      cursor: 'pointer',
                      fontSize: '13px',
                      textAlign: 'left',
                      fontWeight: '500',
                      transition: 'opacity 0.2s'
                    }}
                    onMouseOver={(e) => e.target.style.opacity = '0.9'}
                    onMouseOut={(e) => e.target.style.opacity = '1'}
                  >
                    {service.title}
                  </button>
                ))}
              </div>
            )}

            {/* Lead Form */}
            {showLeadForm && (
              <div style={{ marginTop: '12px', background: 'white', padding: '16px', borderRadius: '8px' }}>
                <form onSubmit={handleLeadSubmit}>
                  <input
                    type="text"
                    placeholder="Your Name *"
                    value={leadData.name}
                    onChange={(e) => setLeadData({ ...leadData, name: e.target.value })}
                    style={{ width: '100%', padding: '8px', marginBottom: '8px', border: '1px solid #ddd', borderRadius: '4px' }}
                    required
                  />
                  <input
                    type="email"
                    placeholder="Email *"
                    value={leadData.email}
                    onChange={(e) => setLeadData({ ...leadData, email: e.target.value })}
                    style={{ width: '100%', padding: '8px', marginBottom: '8px', border: '1px solid #ddd', borderRadius: '4px' }}
                    required
                  />
                  <input
                    type="tel"
                    placeholder="Phone *"
                    value={leadData.phone}
                    onChange={(e) => setLeadData({ ...leadData, phone: e.target.value })}
                    style={{ width: '100%', padding: '8px', marginBottom: '8px', border: '1px solid #ddd', borderRadius: '4px' }}
                    required
                  />
                  <select
                    value={leadData.destination_country}
                    onChange={async (e) => {
                      const value = e.target.value;
                      setLeadData({ ...leadData, destination_country: value, schengen_country: '' });
                      
                      // If Schengen is selected, fetch and show Schengen countries dropdown
                      if (value.toLowerCase() === 'schengen') {
                        await fetchSchengenCountries();
                        setShowSchengenDropdown(true);
                      } else {
                        setShowSchengenDropdown(false);
                      }
                    }}
                    style={{ width: '100%', padding: '8px', marginBottom: '8px', border: '1px solid #ddd', borderRadius: '4px', color: leadData.destination_country ? '#000' : '#999' }}
                    required
                  >
                    <option value="" disabled>Destination Country *</option>
                    {countries.map((country, index) => (
                      <option key={index} value={country.name}>
                        {country.name}
                      </option>
                    ))}
                  </select>
                  
                  {/* Schengen Country Dropdown - shown only when Schengen is selected */}
                  {showSchengenDropdown && (
                    <select
                      value={leadData.schengen_country}
                      onChange={(e) => setLeadData({ ...leadData, schengen_country: e.target.value })}
                      style={{ width: '100%', padding: '8px', marginBottom: '8px', border: '1px solid #ddd', borderRadius: '4px', color: leadData.schengen_country ? '#000' : '#999' }}
                      required
                    >
                      <option value="" disabled>Select Schengen Country *</option>
                      {schengenCountries.map((country, index) => (
                        <option key={index} value={country.name}>
                          {country.name}
                        </option>
                      ))}
                    </select>
                  )}
                  
                  <input
                    type="text"
                    placeholder="Company (Optional)"
                    value={leadData.company}
                    onChange={(e) => setLeadData({ ...leadData, company: e.target.value })}
                    style={{ width: '100%', padding: '8px', marginBottom: '12px', border: '1px solid #ddd', borderRadius: '4px' }}
                  />
                  <button
                    type="submit"
                    disabled={isLoading}
                    style={{
                      width: '100%',
                      background: 'linear-gradient(135deg, #A08EA8 0%, #7D6882 100%)',
                      color: 'white',
                      border: 'none',
                      padding: '10px',
                      borderRadius: '6px',
                      cursor: isLoading ? 'not-allowed' : 'pointer',
                      fontWeight: '600'
                    }}
                  >
                    {isLoading ? 'Submitting...' : 'Submit'}
                  </button>
                </form>
              </div>
            )}

            {/* Agent Connection Option */}
            {showAgentOption && (
              <div style={{ marginTop: '12px', display: 'flex', gap: '8px' }}>
                <button
                  onClick={handleConnectToAgent}
                  style={{
                    flex: 1,
                    background: 'linear-gradient(135deg, #A08EA8 0%, #7D6882 100%)',
                    color: 'white',
                    border: 'none',
                    padding: '10px 16px',
                    borderRadius: '6px',
                    cursor: 'pointer',
                    fontWeight: '600',
                    fontSize: '14px'
                  }}
                >
                  Yes, Connect Me
                </button>
                <button
                  onClick={handleDeclineAgent}
                  style={{
                    flex: 1,
                    background: '#f3f4f6',
                    color: '#6b7280',
                    border: '1px solid #d1d5db',
                    padding: '10px 16px',
                    borderRadius: '6px',
                    cursor: 'pointer',
                    fontWeight: '600',
                    fontSize: '14px'
                  }}
                >
                  No, Thanks
                </button>
              </div>
            )}

            <div ref={messagesEndRef} />
          </div>

          {/* Input - Only show if agent connected or initial state */}
          {(!waitingForAgent && !showLeadForm) && (
            <form onSubmit={sendMessage} style={{ padding: '12px', borderTop: '1px solid #e5e7eb', background: 'white' }}>
              <div style={{ display: 'flex', gap: '8px' }}>
                <input
                  type="text"
                  value={inputMessage}
                  onChange={(e) => setInputMessage(e.target.value)}
                  placeholder="Type a message..."
                  style={{
                    flex: 1,
                    padding: '10px 12px',
                    border: '1px solid #d1d5db',
                    borderRadius: '8px',
                    fontSize: '14px',
                    outline: 'none'
                  }}
                  disabled={isLoading}
                />
                <button
                  type="submit"
                  disabled={isLoading || !inputMessage.trim()}
                  style={{
                    background: 'linear-gradient(135deg, #A08EA8 0%, #7D6882 100%)',
                    color: 'white',
                    border: 'none',
                    padding: '10px 20px',
                    borderRadius: '8px',
                    cursor: isLoading || !inputMessage.trim() ? 'not-allowed' : 'pointer',
                    fontSize: '14px',
                    opacity: isLoading || !inputMessage.trim() ? 0.5 : 1
                  }}
                >
                  <Send size={16} />
                </button>
              </div>
            </form>
          )}
        </div>
      )}
    </>
  );
};

export default ChatbotWidget;
